interface PipelineFiltersProps {
  showFilters: boolean;
  resultFilter: string;
  branchFilter: string;
  requestedByFilter: string;
  uniqueResults: string[];
  uniqueRequesters: string[];
  hasActiveFilters: boolean;
  onResultFilterChange: (filter: string) => void;
  onBranchFilterChange: (branch: string) => void;
  onRequestedByFilterChange: (user: string) => void;
  onClearFilters: () => void;
}

export function PipelineFilters({
  showFilters,
  resultFilter,
  branchFilter,
  requestedByFilter,
  uniqueResults,
  uniqueRequesters,
  hasActiveFilters,
  onResultFilterChange,
  onBranchFilterChange,
  onRequestedByFilterChange,
  onClearFilters,
}: PipelineFiltersProps) {
  if (!showFilters) {
    return null;
  }

  return (
    <div className="mt-6 bg-gray-50 rounded-lg border border-gray-200 p-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Result Filter */}
        <div>
          <label htmlFor="result-filter" className="block text-sm font-medium text-gray-700 mb-2">
            Result
          </label>
          <select
            id="result-filter"
            value={resultFilter}
            onChange={(e) => onResultFilterChange(e.target.value)}
            className="form-select w-full"
          >
            <option value="all">All Results</option>
            {uniqueResults.map((result) => (
              <option key={result} value={result}>
                {result}
              </option>
            ))}
          </select>
        </div>

        {/* Branch Filter */}
        <div>
          <label htmlFor="branch-filter" className="block text-sm font-medium text-gray-700 mb-2">
            Branch Type
          </label>
          <select
            id="branch-filter"
            value={branchFilter}
            onChange={(e) => onBranchFilterChange(e.target.value)}
            className="form-select w-full"
          >
            <option value="all">All Branches</option>
            <option value="exclude-prs">Exclude PR Builds</option>
            <option value="main-only">Main/Master Only</option>
            <option value="prs-only">PR Builds Only</option>
          </select>
        </div>

        {/* Requested By Filter */}
        <div>
          <label htmlFor="requester-filter" className="block text-sm font-medium text-gray-700 mb-2">
            Requested By
          </label>
          <select
            id="requester-filter"
            value={requestedByFilter}
            onChange={(e) => onRequestedByFilterChange(e.target.value)}
            className="form-select w-full"
          >
            <option value="all">All Requesters</option>
            {uniqueRequesters.map((requester) => (
              <option key={requester} value={requester}>
                {requester}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Active Filters and Clear Button */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-700">Active filters:</span>
            
            {resultFilter !== 'all' && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Result: {resultFilter}
                <button
                  onClick={() => onResultFilterChange('all')}
                  className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-green-400 hover:text-green-600"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
            
            {branchFilter !== 'all' && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                Branch: {branchFilter === 'exclude-prs' ? 'Exclude PR Builds' : branchFilter === 'main-only' ? 'Main/Master Only' : 'PR Builds Only'}
                <button
                  onClick={() => onBranchFilterChange('all')}
                  className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-purple-400 hover:text-purple-600"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
            
            {requestedByFilter !== 'all' && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                Requester: {requestedByFilter}
                <button
                  onClick={() => onRequestedByFilterChange('all')}
                  className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-orange-400 hover:text-orange-600"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}
            
            <button
              onClick={onClearFilters}
              className="inline-flex items-center px-3 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Clear All Filters
            </button>
          </div>
        </div>
      )}
    </div>
  );
}